import { apiClient } from '../client';
import type { ApiResponse, PaginatedResponse } from '../types';

// ---------------------------------------------------------------------------
// Types — field names match the .NET DTOs exactly, including the typo
// "minuteDiference" (one 'f') that is preserved for wire compatibility.
// ---------------------------------------------------------------------------

/**
 * Winning tow assignment details inside DetailClaimDTO.towInformation. Mirrors
 * BLL/DTOs/Catalogs/ClaimsDTOs.cs `ResponseTowInformationClaimDTO` (lines 177-190).
 * Null when no offer has been accepted yet.
 */
export interface TowInformation {
  /** Internal identifier for the winning offer row. */
  offerTowId: number;
  /** Tow provider company name. */
  towProvider: string;
  /** Tow provider catalog id — used to link to /catalogs/towing-providers/{id}. */
  towProviderId: number | null;
  /** ISO date string from .NET DateTime? — when the provider responded with their offer. */
  responseDate: string | null;
  /** .NET `string` (not numeric) — preserved as the wire shape. */
  offer: string;
  /** ISO date string from .NET DateTime? — when this offer was selected as winner. */
  winnerDate: string | null;
  /** Display name of the user inside the tow provider org who responded. */
  towProviderUser: string;
  /** .NET `string` (not numeric) — preserved as the wire shape. */
  distance: string;
  howSelectedWinner: string;
  status: string;
  /** ISO date string from .NET DateTime? — driver's promised arrival time. */
  arrivalDate: string | null;
  /** ISO date string from .NET DateTime? — driver's actual arrival time. */
  realArriveDate: string | null;
}

/**
 * Single history event inside DetailClaimDTO.history. Mirrors
 * BLL/DTOs/Catalogs/ClaimsDTOs.cs `ResponseHistoryDTO` (lines 861-868).
 */
export interface HistoryEntry {
  /** ISO date string from .NET DateTime — non-nullable on the .NET side. */
  date: string;
  /** Email address of the user who performed the action. */
  email: string;
  /** Raw userType string from the .NET side (e.g. "Administrator", "Adjuster"). */
  userType: string;
  /** Action identifier — resolved via UPDATE-CLAIM.SECTIONS.history.ACTIONS catalog. */
  action: string;
  /** Free-text description written by the system or user at the time of the action. */
  description: string;
}

/**
 * Offer row inside DetailClaimDTO.offerInformation. Mirrors
 * BLL/DTOs/Catalogs/ClaimsDTOs.cs `offerInformation` class (lines 887-909).
 * Currency fields (offer) are stored as integers; rate fields are decimals.
 * Status is a free-form string from CmOfferTow.status — render via the
 * offerStatusColorMap in TowRequestDetail.tsx.
 *
 * Notes on nullability:
 *   - `creationDate` and `createdBy` are non-nullable in the .NET class but
 *     are typed nullable here to be defensive against partial API responses.
 */
export interface OfferInformation {
  offerTowId: number;
  claimId: number | null;
  towProviderId: number | null;
  userId: number | null;
  responseDate: string | null;
  distance: number | null;
  offer: number | null;
  status: string | null;
  userWinnerId: number | null;
  winnerDate: string | null;
  arrivalDate: string | null;
  creationDate: string | null;
  createdBy: number | null;
  lastUpdateDate: string | null;
  lastUpdatedBy: number | null;
  requestTowId: number | null;
  nameTowProvider: string | null;
  rateInternal: number | null;
  rateGoogle: number | null;
  declineComment: string | null;
}

/**
 * Multimedia file attachment inside DetailClaimDTO.photoVideoAudio. Mirrors
 * BLL/DTOs/Catalogs/ClaimsDTOs.cs `ResponsePhotoVideoAudioDTO` (lines 199-209).
 */
export interface PhotoVideoAudioEntry {
  /** Internal identifier for this multimedia file row — guaranteed unique by the backend. */
  claimFileId: number;
  /** PHOTO | VIDEO | AUDIO | FRONTDRIVER | FRONTPASSENGER | REARDRIVER | REARPASSENGER | DRIVERLICENSE — drives icon and label rendering. */
  fileType: string;
  /** Original filename as stored by the backend. */
  fileName: string;
  /** Display name of the user who uploaded. */
  userUpload: string;
  /** True = restricted to Adjuster role only; false = visible to all roles on the claim. */
  onlyViewAdjuster: boolean;
  /** Backend-issued URL or path to the source file. Not rendered in this slice; later gallery slice will use it for the lightbox. */
  sourceFile: string;
  /** ISO date string from .NET DateTime — non-nullable on the .NET side. */
  creationDate: string;
  /**
   * Slice 1317 — witness FK (slice 1305 backend `CmClaimFile.witnessesId`).
   * When set, this file belongs to a specific witness in `CmWitnesses` rather
   * than the claim or another driver. The slice 1320 PhotoVideoAudioTab
   * groups files by this field into a dedicated witness section.
   */
  witnessesId: number | null;
}

/**
 * Single document entry inside ClaimDocuments.documentMandatory or .documentOther.
 * Mirrors BLL/DTOs/Catalogs/ClaimsDTOs.cs `ClaimDocumentsDTO` (lines 220-231).
 */
export interface ClaimDocument {
  /** Internal identifier for this document row — guaranteed unique by the backend. */
  documentId: number;
  /** Free-form document type label (e.g. "Insurance Policy", "Police Report", "Tow Report"). */
  documentType: string;
  /** APPROVED | REJECTED | PENDING | (free-form) — render via Chip. */
  status: string;
  /** Name or identifier of the party receiving the document. */
  recipient: string;
  /** Name or identifier of the party sending the document. */
  sender: string;
  /** ISO date string from .NET DateTime?. Null when no request date has been set. */
  requestDate: string | null;
  /** Free-text follow-up note or date attached to this document row. */
  followUp: string;
  /** True = restricted to Adjuster role only. .NET type is `bool?` — nullable. */
  onlyViewAdjuster: boolean | null;
  /** True = a file has been uploaded against this document; false = still pending upload. Not rendered in this slice. */
  isUploadFile: boolean;
}

/**
 * Two-list document container inside DetailClaimDTO.documents. Mirrors
 * BLL/DTOs/Catalogs/ClaimsDTOs.cs `ResponseClaimDocumentsDTO` (lines 193-197).
 */
export interface ClaimDocuments {
  /** Required documents the claim must collect (e.g. Insurance Policy, Police Report). */
  documentMandatory: ClaimDocument[] | null;
  /** Optional / supplementary documents. */
  documentOther: ClaimDocument[] | null;
}

/**
 * Other-vehicle driver record inside DetailClaimDTO.otherDrivers. Mirrors
 * BLL/DTOs/Catalogs/ClaimsDTOs.cs `ClaimOtherDriverDTO` (lines 234-249).
 * Only the 8 scalar fields are typed here; the nested file structures are
 * left `unknown` until the file-gallery slice renders them.
 */
export interface ClaimOtherDriver {
  /** Internal identifier for this other-driver row — guaranteed unique by the backend. */
  otherDriverId: number;
  /** Full name of the other driver. */
  name: string;
  /** Contact phone number of the other driver. */
  phone: string;
  /** "brand" on the wire; displayed as Make in the UI (matches the existing Unit Information convention). */
  brand: string;
  /** Vehicle model of the other driver's vehicle. */
  model: string;
  /** Vehicle identification number for the other driver's vehicle. */
  vin: string;
  /** Number of passengers in the other vehicle; null when not recorded. */
  passengerQty: number | null;
  /** Free-text mark/note — typed here for completeness; not rendered in this slice. */
  mark: string;
  /** Nested file structure (5 source URL strings) — typed `unknown` here; a later slice will render the file gallery. */
  files: unknown;
  /** Nested file list (file metadata records) — typed `unknown` here; a later slice will render the file gallery. */
  filesV2: unknown;
}

/**
 * Slice 1325 — lightweight witness summary on `DetailClaimDTO.witnesses`
 * (slice 1324 backend `ClaimWitnessSummaryDTO`). Just the FK + display name —
 * `mediaCount` / `statement` / `phone` stay on the dedicated witness
 * endpoints (`GET /api/v1/Witnesses/GetList`). Used by the slice 1326
 * PhotoVideoAudioTab to resolve `witnessesId` → `fullName` for the Witness
 * column label.
 */
export interface ClaimWitnessSummary {
  witnessesId: number;
  fullName: string;
}

/**
 * Top-level DTO returned by GET /api/v1/TowRequest/Get-Details.
 *
 * Fields used by the read-only detail page are typed precisely.
 * Heavy nested arrays/objects that later slices will consume are typed as
 * `unknown` here intentionally — they cannot be `any` because we want type
 * errors if callers try to use them without a proper cast.
 *
 * @see Backend/LILNS_ITOW_DES/BLL/DTOs/Catalogs/ClaimsDTOs.cs:94-174
 */
/** One face of a driver's licence, as the server holds it. */
export interface DriverLicenseSide {
  onFile: boolean;
  /** ISO date. Null when nothing is on file. */
  capturedOn: string | null;
  /** Signed URL, good for an hour. Null when nothing is on file. */
  url: string | null;
  /** A PDF cannot be shown as an image; the page links to it instead. */
  isPdf: boolean;
}

/**
 * The licence document a driver submitted from the app — `DriverLicenseFileDTO`.
 *
 * `onFile` is the server's answer to "do we have what we need": both faces, or
 * a single PDF, which is the whole document.
 */
export interface DriverLicenseFile {
  onFile: boolean;
  front: DriverLicenseSide | null;
  back: DriverLicenseSide | null;
}

/**
 * The tow request itself, beside the claim it belongs to. `DetailClaimDTO.status`
 * is the claim's status (OPEN/CLOSE); this one is the request's.
 */
export interface TowRequestCore {
  /** DRAFT | INCOMING | OPEN | NOBIDS | WINNER | TOARRIVE | INPROCESS | COMPLETED | CANCEL | ABANDONED */
  status: string;
  requestTowId: number;
  claimId: number;
  customerId: number;
  towProviderId: number | null;
  offer: number;
  /** When bidding opened. Absent on responses from an API older than 2026-09-15. */
  requestDate?: string | null;
}

export interface DetailClaimDTO {
  claimId: number;
  claimNumber: string;
  /** .NET `string` (not DateTime) — contains an ISO date string at runtime. */
  claimDate: string | null;
  customerId: number | null;
  customer: string;
  driverId: number | null;
  driver: string;
  /**
   * The licence document itself, as opposed to the typed licence fields below.
   * Absent on responses from an API older than 2026-09-11.
   */
  driverLicense?: DriverLicenseFile | null;
  unitId: number | null;
  unit: string;
  /** OPEN | INPROCESS | TOARRIVE | WINNER | COMPLETED | CLOSE | CANCEL */
  status: string;
  adjusterId: number | null;
  adjuster: string;
  /** Typo "adjuser" preserved from .NET source — wire field name must match. */
  adjuserUserId: number | null;
  adjusterUser: string;
  fullNameDriver: string;
  // Vehicle / Unit
  instanceNumber: string;
  serialNumber: string;
  /** "brand" on the wire; displayed as Make in the UI. */
  brand: string;
  model: string;
  year: number | null;
  plates: string;
  weight: number | null;
  expirationPolicy: string | null;
  // Other top-level fields not rendered in this slice — included for completeness
  licenseStateDriver: string;
  licenseNumberDriver: string;
  licenseClassDriver: string;
  expirationLicenseDriver: string | null;
  medicoPreventivaDriver: string;
  /**
   * Social Security Number of the claim driver.
   * Typed matching the .NET property name `SSNDriver` (all-caps acronym).
   * ASP.NET Core CamelCase policy may lowercase it to `ssnDriver` on the wire —
   * to verify once the dev DB is reachable (slice 210 assumption).
   */
  SSNDriver: string;
  emailDriver: string;
  phoneDriver: string;
  weather: string;
  latitude: string;
  longitude: string;
  requestDate911: string | null;
  weatherCheck: string;
  policeReport: string;
  policeReportDate: string | null;
  descriptionAccident: string;
  /**
   * Slice 1317 — Flujo V2 wizard answers (slice 1276 backend columns,
   * slice 1304 read-projection). The wizard captures these on-scene; the
   * slice 1319 PoliceInformationTab surfaces them as read-only labels.
   * String code values map to localized display via slice 1318 enumLabel
   * helper. Always nullable — only Flujo-submitted claims populate them.
   */
  safetyStatus: string | null;       // "safe" | "danger"
  injuryLevel: string | null;        // "none" | "minor" | "severe" | "unsure"
  policeStatus: string | null;       // "yes" | "in-progress" | "no"
  wantsTow: boolean | null;
  towServiceType: string | null;     // "light" | "flatbed" | "heavy"
  /**
   * Where the vehicle actually is and what the operator should know before
   * arriving — the driver's own words, straight off the mobile request.
   *
   * These reached the server inside `descriptionAccident` until now, joined
   * with em dashes, so the page rendered "heavy — Hopital — Pick up at
   * hopital" as **How accident occurred** in the police block. They render
   * with the map instead, which is the question they answer. Null on every
   * request filed before the split, and on any raised from the web.
   */
  landmark: string | null;
  driverNotes: string | null;
  /**
   * Slice 1325 — witness summaries (slice 1324 backend addition). The
   * slice 1326 PhotoVideoAudioTab Witness column resolves each file's
   * `witnessesId` to a `fullName` via this list. Null/empty when the
   * claim has no witnesses.
   */
  witnesses: ClaimWitnessSummary[] | null;
  closeDate: string | null;
  // Heavy nested data — typed as `unknown` until later slices need them.
  // Do NOT cast to `any`; add a typed interface in the slice that consumes them.
  towInformation: TowInformation | null;
  unitDetail: unknown;
  driverDetail: unknown;
  otherDrivers: ClaimOtherDriver[] | null;
  photoVideoAudio: PhotoVideoAudioEntry[] | null;
  documents: ClaimDocuments | null;
  history: HistoryEntry[] | null;
  /**
   * Wire names here are camelCase, which is what this API actually emits. The
   * two below were declared PascalCase and nothing ever read them, so the
   * mismatch stayed invisible until the roadside screen tried to.
   */
  cmTowRequest: TowRequestCore | null;
  offerInformation: OfferInformation[] | null;
  /**
   * The roadside request itself. Typed now: a roadside job has no claim, so the
   * detail screen has to read this rather than the claim columns beside it —
   * reading those is why that page rendered a column of em dashes.
   */
  srServiceRequest?: ServiceRequestDetail | null;
  offerInformationService: unknown;
}

/**
 * A roadside request — `CmTowServiceRequestDTO` on the wire.
 *
 * A roadside job is not filed against a claim (a lockout or a flat is not an
 * insurance matter), so none of the claim-shaped fields on DetailClaimDTO apply
 * to one. Everything the roadside detail screen shows comes from here.
 */
export interface ServiceRequestDetail {
  status: string | null;
  serviceRequestId: number;
  customerId: number;
  towProviderId: number | null;
  offer: number;
  /** One of ROADSIDE_SERVICES — what was asked for. */
  serviceCode: string | null;
  description: string | null;
  /**
   * Where the vehicle actually is, and what the operator should know before
   * setting off. Collected by the app on a roadside request just as on a tow.
   */
  landmark: string | null;
  driverNotes: string | null;
  latitudeGPS: string | null;
  longitudeGPS: string | null;
  /**
   * The conditions at the scene, as a sentence. The roadside twin of the tow
   * screen's `weather` — it comes off the service request's own column rather
   * than out of the claim weather table, because roadside opens no claim.
   */
  weather: string | null;
  weatherIcon: string | null;
  customerName: string | null;
  providerName: string | null;
  driverId: number | null;
  driverName: string | null;
  driverPhone: string | null;
  requestDate: string | null;
  creationDate: string | null;
  winnerDate: string | null;
  startServiceDate: string | null;
  realArriveDate: string | null;
  deliveredDate: string | null;
  closeDate: string | null;
}

export interface PoTowRateDetails {
  rateId: number;
  creationDate: string | null;
  createdBy: number;
  lastUpdateDate: string | null;
  lastUpdatedBy: number | null;
  towProviderId: number;
  sourceId: number;
  sourceType: string;
  origin: string;
  userId: number | null;
  rate: number;
  comment: string;
  enabledFlag: boolean;
}

export interface GetListTowRequestDTO {
  requestTowId: number;
  creationDate: string | null;
  createdBy: number;
  lastUpdateDate: string | null;
  lastUpdatedBy: number | null;
  claimId: number | null;
  requestDate: string | null;
  customerId: number | null;
  towProviderId: number | null;
  status: string;
  offerTowId: number | null;
  offer: number | null;
  winnerDate: string | null;
  realArriveDate: string | null;
  deliveredDate: string | null;
  closeDate: string | null;
  /** Preserved typo from backend — wire field is "minuteDiference" (one 'f'). */
  minuteDiference: number | null;
  nameCustomer: string;
  nameTowProvider: string;
  claimNumber: string;
  rateData: PoTowRateDetails | null;
  counterProviders: number | null;
  serviceType: string;
}

/**
 * Query parameters for GET /api/v1/TowRequest/Get-List-Admin.
 *
 * All fields are optional — ASP.NET applies server-side defaults for any
 * omitted field. Send only the fields you have; never send undefined keys.
 */
export interface TowRequestsListFilters {
  claimNumber?: string;
  /** OPEN | INPROCESS | TOARRIVE | WINNER | COMPLETED | CLOSE | CANCEL */
  status?: string;
  /** Server default: 0 = no filter */
  customerId?: number;
  /** Server default: 0 = no filter */
  towProviderId?: number;
  /** .NET long? — nullable */
  userId?: number | null;
  serviceType?: string;
  type?: string;
  /** Defaults false on the server; set true for dashboard drill-down context */
  isDashboard?: boolean;
  /** ISO date string from .NET DateTime? */
  startDate?: string;
  /** ISO date string from .NET DateTime? */
  endDate?: string;
  /**
   * Filter by adjuster ID. Added in slice 114 to power the AdjusterDetail
   * recent-tow-requests sub-table. Maps to the slice-113 backend `long? adjusterId`
   * predicate that navigates through `CmTowRequest.CmClaims.adjusterId`.
   */
  adjusterId?: number;
  /**
   * Filter by driver ID. Added in slice 125 to power the DriverDetail
   * recent-tow-requests sub-table. Maps to the slice-124 backend `long? driverId`
   * predicate that navigates through `CmTowRequest.CmClaims.driverId`.
   */
  driverId?: number;
  /**
   * Filter by unit ID. Added in slice 127 to power the UnitDetail
   * recent-tow-requests sub-table. Maps to the slice-126 backend `long? unitId`
   * predicate that navigates through `CmTowRequest.CmClaims.unitId`.
   */
  unitId?: number;
  /**
   * 0-indexed page number; backend signature is `int? page`. Defaults to 0
   * server-side when omitted. Added in slice 128 alongside server-side
   * pagination on `Get-List-Admin`.
   */
  page?: number;
  /**
   * Optional page size. Backend signature is `int? pageSize`; when omitted the
   * BLL falls back to 20 (matches the slice-87 Lookup pattern).
   */
  pageSize?: number;
  /** Column to sort by — sent to the backend (slice 374). */
  orderBy?: string;
  /** Sort direction — 'asc' or 'desc' (slice 374). */
  orderDirection?: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// Endpoint helper
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/TowRequest/Get-List-Admin
 *
 * Despite the "Admin" suffix the BLL routes results by the caller's JWT
 * `userType` + `userId` + `sourceTypeId`, so each role sees only their own
 * slice of data (Customer → their requests, TowProvider → their offers/jobs,
 * Administrator → everything).
 *
 * When called with no arguments, no query params are sent and the server
 * applies its own defaults. When filters are supplied, they are attached via
 * axios `params` (undefined values are omitted automatically by axios).
 *
 * Requires Bearer auth (attached automatically by the apiClient interceptor).
 *
 * Slice 128: backend now returns `Pagination<List<GetListTowRequestDTO>>`
 * (the `Response<T>.data` is the `PaginatedResponse` envelope). Callers
 * read `.elements` for the rows and `.totalRecords`/`.page`/`.pageSize`
 * for pagination UI.
 *
 * @returns Unwrapped `PaginatedResponse<GetListTowRequestDTO>` — callers do
 *          not need to access `.data` again.
 */
export async function getTowRequestsList(
  filters?: TowRequestsListFilters,
): Promise<PaginatedResponse<GetListTowRequestDTO>> {
  const response = await apiClient.get<
    ApiResponse<PaginatedResponse<GetListTowRequestDTO>>
  >(
    '/api/v1/TowRequest/Get-List-Admin',
    filters !== undefined ? { params: filters } : undefined,
  );
  return response.data.data;
}

/** Body for POST /api/v1/TowRequest/Register. */
export interface RegisterServiceRequestPayload {
  /** TOWING | ROADSIDE ASSISTANCE — the driver's own answer to what they need. */
  serviceType: string;
  /** Which vehicle needs help. Omit only when `claimId` names a claim that already records it. */
  unitId?: number;
  /** Whose request this is. Omit as a driver — the server uses your own record and ignores this. */
  driverId?: number;
  /** Omit only when `claimId` names a claim that already carries coordinates. */
  latitude?: string;
  longitude?: string;
  /** What is wrong, in the driver's words. */
  description?: string;
  /** Roadside only — one of ROADSIDE_SERVICES, the same codes providers offer. */
  roadsideServiceCode?: string;
  /** Attach the tow to an existing claim instead of opening one. Towing only. */
  claimId?: number;
  /** Optional; the server leaves it null when absent rather than inventing one. */
  claimNumber?: string;
}

/** What POST /api/v1/TowRequest/Register created. */
export interface RegisterServiceRequestResult {
  serviceType: string;
  /** requestTowId for towing, serviceRequestId for roadside — both open the same detail screen. */
  requestId: number;
  /** The claim this opened or attached to. Null for roadside, which has no claim. */
  claimId: number | null;
  /** Zero means nobody covers this location and the administrators were emailed instead. */
  providersAlerted: number;
}

/**
 * POST /api/v1/TowRequest/Register — raise a request for help.
 *
 * One endpoint for both kinds, open to the driver who needs the truck as well
 * as to dispatch. Raising a tow is what opens the claim: the accident has just
 * happened when the request comes in, so there is nothing to attach to yet, and
 * filing a claim is gated on a menu permission a driver's token does not carry.
 * Roadside never opens a claim — a lockout or a flat is not an insurance matter.
 *
 * The server decides whose request it is: a driver can only ever raise their
 * own, whatever `driverId` says.
 */
export async function registerServiceRequest(
  payload: RegisterServiceRequestPayload,
): Promise<RegisterServiceRequestResult> {
  const response = await apiClient.post<ApiResponse<RegisterServiceRequestResult>>(
    '/api/v1/TowRequest/Register',
    payload,
  );
  return response.data.data;
}

/**
 * GET /api/v1/TowRequest/Get-List-Driver — the requests raised for the
 * signed-in driver's own vehicle.
 *
 * `Get-List-Admin` routes by `userType`, but its "not a tow provider" branch is
 * the Administrator branch — so a driver calling it was answered with every tow
 * request in the tenant, filterable by customer and by provider. This action
 * gates on the token's `userType` and scopes on its `sourceTypeId` (the
 * driverId), which is strictly narrower.
 *
 * Same response shape as {@link getTowRequestsList}, so callers only pick a URL.
 * `counterProviders` comes back null — the count of alerted providers is
 * bidding-desk information the driver's list does not render.
 */
export async function getDriverTowRequestsList(
  filters?: TowRequestsListFilters,
): Promise<PaginatedResponse<GetListTowRequestDTO>> {
  const response = await apiClient.get<
    ApiResponse<PaginatedResponse<GetListTowRequestDTO>>
  >(
    '/api/v1/TowRequest/Get-List-Driver',
    filters !== undefined ? { params: filters } : undefined,
  );
  return response.data.data;
}

/** The answer to "when will the truck get here", and how much to trust it. */
export interface ProviderArrival {
  /**
   * LIVE — a recent position from the provider's app.
   * STALE — a position, but old enough not to read as current.
   * PROMISED — no position; this is what they committed to when bidding.
   * NONE — no provider assigned yet, so there is nothing to say.
   */
  source: 'LIVE' | 'STALE' | 'PROMISED' | 'NONE';
  providerName: string | null;
  latitude: string | null;
  longitude: string | null;
  /** Minutes to arrival from the device's router, when it reported one. */
  etaMinutes: number | null;
  reportedDate: string | null;
  positionAgeMinutes: number | null;
  /** What the provider committed to on winning the bid. */
  promisedArrival: string | null;
  /** True when the provider has never reported. Expected, not a fault. */
  providerNotReporting: boolean;
}

/**
 * GET /api/v1/TowRequest/Get-Arrival — when the truck is expected.
 *
 * The only arrival time this system held was the one the provider promised when
 * they bid, made before the truck moved. This adds the live answer when the
 * provider is running the app, keeps returning a stale position with its age
 * rather than hiding it, and falls back to the promise — labelled as a promise —
 * for the many providers who will not run the app at all.
 */
export async function getProviderArrival(
  requestId: number,
  serviceType: string,
): Promise<ProviderArrival> {
  const response = await apiClient.get<ApiResponse<ProviderArrival>>(
    '/api/v1/TowRequest/Get-Arrival',
    { params: { requestId, serviceType } },
  );
  return response.data.data;
}

/**
 * PUT /api/v1/TowRequest/Start-Service-Request?timeZoneOffset={offset}
 *
 * Marks a roadside job as under way: status becomes TOARRIVE and
 * `startServiceDate` is stamped. The truck is rolling.
 *
 * The body is wrapped in an `offer` object because the controller binds
 * `offerServiceData`, whose only member is the offer — sending the offer at the
 * top level binds an empty model and silently updates nothing.
 */
export async function startServiceRequest(payload: {
  serviceRequestId: number;
  towProviderId: number;
  userId: number;
}): Promise<boolean> {
  const response = await apiClient.put<ApiResponse<boolean>>(
    '/api/v1/TowRequest/Start-Service-Request',
    { offer: { ...payload, createdBy: payload.userId } },
    { params: { timeZoneOffset: new Date().getTimezoneOffset() } },
  );
  return response.data.data;
}

/**
 * PUT /api/v1/TowRequest/Service-Request-Update?timeZoneOffset={offset}&id={id}
 *
 * Closes a roadside job — status CLOSE, `deliveredDate` and `closeDate`
 * stamped. Despite the general-sounding name this is the completion call and
 * the only status transition the roadside path has beyond starting it.
 */
export async function completeServiceRequest(serviceRequestId: number): Promise<boolean> {
  const response = await apiClient.put<ApiResponse<boolean>>(
    '/api/v1/TowRequest/Service-Request-Update',
    null,
    {
      params: {
        timeZoneOffset: new Date().getTimezoneOffset(),
        id: serviceRequestId,
      },
    },
  );
  return response.data.data;
}

/**
 * GET /api/v1/TowRequest/Get-Details?requestTowId={id}&serviceType={type}
 *
 * Both query-param names are pinned by [FromQuery(Name="...")] decorators on
 * the .NET controller — send exactly `requestTowId` and `serviceType`.
 *
 * @returns Unwrapped `DetailClaimDTO` — callers do not need to access `.data`.
 */
export async function getTowRequestDetail(
  id: number,
  serviceType: string,
): Promise<DetailClaimDTO> {
  const response = await apiClient.get<ApiResponse<DetailClaimDTO>>(
    '/api/v1/TowRequest/Get-Details',
    { params: { requestTowId: id, serviceType } },
  );
  return response.data.data;
}

/**
 * Wire body for Accept-Offer / Update-Offer-Decline. Backend wraps the
 * actual fields under `{ offer: ... }` (see TowRequestController action
 * signatures). The shape mirrors requestAcceptOfferDTO from
 * BLL/DTOs/Catalogs/TowRequestDTOs.cs.
 */
export interface AcceptOrDeclineOfferPayload {
  offerTowId: number;
  claimId: number | null;
  towProviderId: number | null;
  userId: number | null;
  responseDate: string | null;
  distance: number | null;
  offer: number | null;
  status: string | null;
  userWinnerId: number | null;
  winnerDate: string | null;
  arrivalDate: string | null;
  creationDate: string | null;
  createdBy: number | null;
  requestTowId: number | null;
  nameTowProvider: string | null;
  declineComment: string | null;
}

/**
 * POST /api/v1/TowRequest/Accept-Offer
 *
 * Accepts a tow provider's offer. The controller action takes an `offerData`
 * wrapper named `{ offer: ... }`. Returns true on success.
 */
export async function acceptOffer(
  payload: AcceptOrDeclineOfferPayload,
): Promise<boolean> {
  const response = await apiClient.post<ApiResponse<boolean>>(
    '/api/v1/TowRequest/Accept-Offer',
    { offer: payload },
  );
  return response.data.data;
}

/**
 * PUT /api/v1/TowRequest/Update-Offer-Decline
 *
 * Declines a tow provider's offer. The controller action takes an `offerData`
 * wrapper named `{ offer: ... }`. `declineComment` is required by the backend
 * BLL; caller must populate it before calling this function. Returns true on success.
 */
export async function declineOffer(
  payload: AcceptOrDeclineOfferPayload,
): Promise<boolean> {
  const response = await apiClient.put<ApiResponse<boolean>>(
    '/api/v1/TowRequest/Update-Offer-Decline',
    { offer: payload },
  );
  return response.data.data;
}

/**
 * POST /api/v1/TowRequest/Register-Offer
 *
 * Creates a new offer. `offerTowId` in the payload must be 0 — the backend
 * generates and returns the new ID. Returns the new `offerTowId` on success.
 * The payload type is the same `AcceptOrDeclineOfferPayload` used for
 * accept/decline because the backend takes one DTO for all four actions
 * (Register / Accept / Update / Update-Decline).
 *
 * The body is sent FLAT — this is the one offer action that is not wrapped.
 * `RegisterOffer` binds `requestAcceptOfferDTO` directly, while Accept-Offer,
 * Update-Offer and Update-Offer-Decline bind `offerData`, whose only member is
 * `offer`. Wrapping this one made the server read `int offer` and find `{`,
 * answering `Unexpected character encountered while parsing value: {. Path
 * 'offer', line 1, position 10.` So submitting a new bid from this client has
 * never once succeeded; editing and accepting always did, which is why the
 * shape looked right. Angular posts its form value flat here and works.
 */
export async function registerOffer(
  payload: AcceptOrDeclineOfferPayload,
): Promise<number> {
  const response = await apiClient.post<ApiResponse<number>>(
    '/api/v1/TowRequest/Register-Offer',
    payload,
  );
  return response.data.data;
}

/**
 * PUT /api/v1/TowRequest/Update-Offer
 *
 * Updates an existing offer (`offerTowId` required). Returns true on success.
 */
export async function updateOffer(
  payload: AcceptOrDeclineOfferPayload,
): Promise<boolean> {
  const response = await apiClient.put<ApiResponse<boolean>>(
    '/api/v1/TowRequest/Update-Offer',
    { offer: payload },
  );
  return response.data.data;
}

/**
 * PUT /api/v1/TowRequest/Cancel?requestTowId={id}
 *
 * Calls off a request. Refused for a request that is already cancelled or
 * already finished, and for callers who do not own it — the server decides,
 * since a customer driver's menu claim cannot express this permission.
 *
 * The reason is optional but reaches the provider who was on their way, so it
 * is worth asking for.
 */
export async function cancelTowRequest(
  requestTowId: number,
  reason: string,
): Promise<boolean> {
  const response = await apiClient.put<ApiResponse<boolean>>(
    `/api/v1/TowRequest/Cancel?requestTowId=${requestTowId}`,
    { reason },
  );
  return response.data.data;
}

// ---------------------------------------------------------------------------
// alertTowProviders — POST /api/v1/ExternalAPI/TowProviders/Tow-Request-Alert
//
// The front door of the bidding loop. Creates the CmTowRequest for a claim and
// emails every enabled provider offering a tow within 150 miles, recording one
// CmTowGroup row each so the request knows who was asked.
//
// The claim supplies the unit, the weather and the map link in that email, so
// a claim with no coordinates alerts providers to a location they cannot see.
// ---------------------------------------------------------------------------

export interface AlertTowProvidersPayload {
  claimId: number;
  /** Where to search from — decimal degrees as strings, as the API expects. */
  latitud: string;
  longitud: string;
  driverId: number;
}

/** Raises a tow request for a claim and alerts nearby providers. */
export async function alertTowProviders(
  payload: AlertTowProvidersPayload,
): Promise<string> {
  const res = await apiClient.post<ApiResponse<string>>(
    '/api/v1/ExternalAPI/TowProviders/Tow-Request-Alert',
    payload,
  );
  return res.data.data;
}
